import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Paper,
  Card,
  CardContent,
  Breadcrumbs,
  Link as MuiLink,
  Snackbar,
  Alert,
  Avatar,
  Fade,
  Slide,
  Divider
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  WhatsApp as WhatsAppIcon,
  NavigateNext as NavigateNextIcon,
  Send as SendIcon,
  AccessTime as AccessTimeIcon,
  Support as SupportIcon,
  Chat as ChatIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import contactBn from '../i18n/bn/contact';
import contactEn from '../i18n/en/contact';
import contactNp from '../i18n/np/contact';
import contactPk from '../i18n/pk/contact';
import contactBt from '../i18n/bt/contact';
import contactSl from '../i18n/sl/contact';
import contactAf from '../i18n/af/contact';
import contactCn from '../i18n/cn/contact';

const Contact = () => {
  const lang = (typeof window !== 'undefined' && localStorage.getItem('preferredLanguage')) || 'bn';
  const t = lang === 'en' ? contactEn : lang === 'np' ? contactNp : lang === 'pk' ? contactPk : lang === 'bt' ? contactBt : lang === 'sl' ? contactSl : lang === 'af' ? contactAf : lang === 'cn' ? contactCn : contactBn;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    setSnackbar({
      open: true,
      message: t.form.success,
      severity: 'success'
    });

    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const contactInfo = [
    {
      icon: <PhoneIcon sx={{ fontSize: 40 }} />,
      title: t.info.service.title,
      primary: '+880 1234 567890',
      secondary: '+880 1234 567891',
      action: 'tel:+8801234567890',
      color: '#4CAF50',
      description: t.info.service.desc
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      title: t.info.email.title,
      primary: 'info@grameenbazar.com',
      secondary: 'support@grameenbazar.com',
      action: 'mailto:info@grameenbazar.com',
      color: '#2196F3',
      description: t.info.email.desc
    },
    {
      icon: <LocationIcon sx={{ fontSize: 40 }} />,
      title: t.info.address.title,
      primary: t.info.address.primary,
      secondary: t.info.address.secondary,
      action: 'https://maps.google.com',
      color: '#FF9800',
      description: t.info.address.desc
    }
  ];

  const socialLinks = [
    {
      icon: <FacebookIcon />,
      label: 'Facebook',
      url: 'https://facebook.com/grameenbazar',
      color: '#1877F2'
    },
    {
      icon: <WhatsAppIcon />,
      label: 'WhatsApp',
      url: 'https://wa.me/8801234567890',
      color: '#25D366'
    }
  ];

  const quickActions = [
    {
      title: t.quick.actions.liveChat.title,
      description: t.quick.actions.liveChat.desc,
      icon: <ChatIcon />,
      color: '#4CAF50',
      action: t.quick.actions.liveChat.cta
    },
    {
      title: t.quick.actions.helpCenter.title,
      description: t.quick.actions.helpCenter.desc,
      icon: <SupportIcon />,
      color: '#2196F3',
      action: t.quick.actions.helpCenter.cta
    },
    {
      title: t.quick.actions.business.title,
      description: t.quick.actions.business.desc,
      icon: <BusinessIcon />,
      color: '#FF9800',
      action: t.quick.actions.business.cta
    }
  ];

  return (
    <>
      <Header />
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 8,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Container>
            <Breadcrumbs 
              separator={<NavigateNextIcon fontSize="small" />} 
              aria-label="breadcrumb"
              sx={{ mb: 4, '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.7)' } }}
            >
              <MuiLink component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {t.breadcrumbs.home}
              </MuiLink>
              <Typography sx={{ color: 'white' }}>{t.breadcrumbs.contact}</Typography>
            </Breadcrumbs>

            <Fade in timeout={1000}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                  {t.hero.title}
                </Typography>
                <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
                  {t.hero.subtitle}
                </Typography>
              </Box>
            </Fade>
          </Container>
        </Box>

        <Container sx={{ py: 6 }}>
          {/* Quick Actions */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.quick.title}
            </Typography>
            <Grid container spacing={4}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Slide direction="up" in timeout={500 + index * 200}>
                    <Card
                      sx={{
                        textAlign: 'center',
                        p: 4,
                        height: '100%',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                        },
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: action.color,
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 3
                        }}
                      >
                        {action.icon}
                      </Avatar>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                        {action.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {action.description}
                      </Typography>
                      <Button
                        variant="contained"
                        sx={{
                          bgcolor: action.color,
                          '&:hover': {
                            bgcolor: action.color,
                            filter: 'brightness(0.9)'
                          }
                        }}
                      >
                        {action.action}
                      </Button>
                    </Card>
                  </Slide>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Contact Info Cards */}
          <Box sx={{ mb: 8 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center', mb: 6 }}>
              {t.info.title}
            </Typography>
            <Grid container spacing={4}>
              {contactInfo.map((info, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Fade in timeout={800 + index * 200}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                        },
                        border: '1px solid #e2e8f0'
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 4 }}>
                        <Avatar
                          sx={{
                            bgcolor: info.color,
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3
                          }}
                        >
                          {info.icon}
                        </Avatar>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                          {info.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {info.description}
                        </Typography>
                        <MuiLink
                          href={info.action}
                          target={info.title === 'ঠিকানা' ? "_blank" : "_self"}
                          rel="noopener noreferrer"
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': {
                              color: info.color
                            }
                          }}
                        >
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                            {info.primary}
                          </Typography>
                          <Typography variant="body1" color="text.secondary">
                            {info.secondary}
                          </Typography>
                        </MuiLink>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Contact Form and Map */}
          <Grid container spacing={6}>
            {/* Contact Form */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1200}>
                <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                    {t.form.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
                    {t.form.desc}
                  </Typography>
                  
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.form.name}
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t.form.email}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label={t.form.phone}
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.form.subject}
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t.form.message}
                          name="message"
                          multiline
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          placeholder={t.form.messagePh}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<SendIcon />}
                          sx={{
                            py: 1.5,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          {t.form.submit}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </Fade>
            </Grid>
            
            {/* Map and Additional Info */}
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1400}>
                <Box>
                  {/* Office Hours */}
                  <Paper sx={{ p: 4, mb: 4, border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <AccessTimeIcon />
                      </Avatar>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {t.hours.title}
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {t.hours.weekdays}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        ৮:০০ - ৫:০০
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                        {t.hours.friday}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                          ১০:০০ - ৭:০০
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                        {t.hours.note}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Google Maps */}
                  <Paper sx={{ height: '300px', mb: 4, overflow: 'hidden', borderRadius: 2 }}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d204.52349725645036!2d89.24624971416927!3d24.36681326014607!2m3!1f0!2f1.847311040165165!3f0!3m2!1i1024!2i768!4f51.26304100774385!5e1!3m2!1sen!2sat!4v1755101078536!5m2!1sen!2sat"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </Paper>

                  {/* Social Links */}
                  <Paper sx={{ p: 4, border: '1px solid #e2e8f0' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                      {t.social.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      {socialLinks.map((social, index) => (
                        <Button
                          key={index}
                          variant="contained"
                          startIcon={social.icon}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            bgcolor: social.color,
                            '&:hover': {
                              bgcolor: social.color,
                              filter: 'brightness(0.9)'
                            }
                          }}
                        >
                          {social.label}
                        </Button>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Fade>
            </Grid>
          </Grid>

          {/* FAQ CTA */}
          <Box sx={{ mt: 8 }}>
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <SupportIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                {t.faq.title}
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                {t.faq.subtitle}
              </Typography>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/help"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'grey.100'
                  }
                }}
              >
                {t.faq.cta}
              </Button>
            </Paper>
          </Box>
        </Container>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
      <Footer />
    </>
  );
};

export default Contact; 